import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight, CheckCircle2, Store, Layout, FolderTree, Settings, Megaphone, Award, Target, RefreshCw, BarChart3, Smartphone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AmazonBrandStore = () => {
  const setupServices = [
    'Creating a customized Amazon Brand Store',
    'Designing professional store layouts and banners',
    'Creating brand-focused storefront design',
    'Adding featured products and collections',
    'Creating a mobile-friendly store structure',
    'Highlighting best-selling and important products'
  ];

  const organizationServices = [
    'Creating product categories and subcategories',
    'Organizing products based on product types or collections',
    'Highlighting best-selling and featured products',
    'Structuring category pages for easy navigation'
  ];

  const optimizationServices = [
    'Store layout improvements',
    'Banner and creative updates',
    'Product placement optimization',
    'Category page improvements',
    'Store performance analysis'
  ];

  const advertisingServices = [
    'Running Sponsored Brands campaigns directing traffic to the Brand Store',
    'Targeting relevant keywords using Amazon ads',
    'Promoting product collections within the storefront',
    'Increasing product discovery and brand engagement'
  ];

  const whyChooseUs = [
    { icon: Store, title: 'Professional Amazon Storefront Design Expertise', description: 'Our team specializes in creating visually stunning and high-converting Brand Stores.' },
    { icon: FolderTree, title: 'Strategic Category & Product Organization', description: 'We structure your store for optimal customer navigation and product discovery.' },
    { icon: Target, title: 'Conversion-Focused Brand Store Layouts', description: 'Every design element is crafted to maximize engagement and conversions.' },
    { icon: Megaphone, title: 'Integration with Amazon Advertising Campaigns', description: 'We connect your Brand Store with strategic ad campaigns for maximum visibility.' },
    { icon: RefreshCw, title: 'Continuous Optimization for Better Performance', description: 'We regularly analyze and improve your store to maintain peak performance.' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Amazon Brand Store Setup & Optimization | Obiquiste Solutions</title>
        <meta name="description" content="Custom Amazon Brand Store design and setup. Showcase your brand, improve customer experience & increase sales. Professional storefront creation services." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/amazon-brand-store-setup" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/amazon-account-management-services" className="text-slate-700 hover:text-primary font-medium">Amazon</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Brand Store Setup</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.03) 0%, rgba(255, 193, 7, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Amazon Brand Store Setup & <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">Optimization</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                Create a Dedicated Storefront. Strengthen Your Brand Identity.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-3xl mx-auto">
                An Amazon Brand Store allows brands to create a dedicated storefront on Amazon where customers can explore their entire product catalog in a structured and visually engaging way. A well-designed Brand Store helps improve product discovery, strengthen brand identity, and increase customer trust.
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-3xl mx-auto">
                At Obiquiste Solutions, we help brands design, structure, and optimize Amazon Brand Stores to improve product visibility and conversions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact?service=amazon-brand-store">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Store Consultation
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=amazon-brand-store">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    View Store Examples
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amazon Brand Store Setup */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-100 rounded-xl mb-6">
                <Store className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Amazon Brand Store Setup
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Setting up an Amazon Brand Store requires proper planning and layout design so customers can easily browse products and collections.
              </p>
              <p className="text-lg text-slate-700 mb-6">
                Our team creates professional storefronts that showcase the brand story and product range effectively. A well-designed storefront improves the overall shopping experience and helps convert visitors into customers.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Setup Services Include:</h3>
                <div className="space-y-4">
                  {setupServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Store Category Structure & Product Organization */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Organization Services:</h3>
                <div className="space-y-4">
                  {organizationServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-blue-800 text-sm">
                    <strong>Result:</strong> Proper organization helps customers explore more products and improves overall engagement.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-6">
                <FolderTree className="w-7 h-7 text-blue-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Brand Store Category Structure & Product Organization
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Proper category structure is essential for helping customers navigate the storefront easily.
              </p>
              <p className="text-lg text-slate-700">
                We organize products into logical categories so visitors can quickly find relevant products. This strategic organization creates a seamless shopping experience that encourages customers to explore your entire product range.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amazon Brand Store Optimization */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-xl mb-6">
                <Settings className="w-7 h-7 text-purple-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Amazon Brand Store Optimization
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                Creating the Brand Store is only the first step. To maximize results, the store must be optimized regularly.
              </p>
              <p className="text-lg text-slate-700">
                We analyze storefront performance and improve the design and structure to increase engagement and conversions. Regular optimization helps improve product discovery and customer experience.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Optimization Services:</h3>
                <div className="space-y-4">
                  {optimizationServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Driving Traffic to Brand Store Using Amazon Ads */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <h3 className="font-heading text-xl font-bold mb-6 text-slate-800">Our Advertising Services:</h3>
                <div className="space-y-4">
                  {advertisingServices.map((item, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-green-800 text-sm">
                    <strong>Impact:</strong> Combining Amazon Ads with a well-structured Brand Store helps improve conversions and brand visibility.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-xl mb-6">
                <Megaphone className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Driving Traffic to Brand Store Using Amazon Ads
              </h2>
              <p className="text-lg text-slate-700 mb-4">
                One of the biggest advantages of an Amazon Brand Store is the ability to drive traffic through Amazon advertising campaigns.
              </p>
              <p className="text-lg text-slate-700">
                Sponsored Brands ads can direct customers directly to the Brand Store, helping increase brand visibility and conversions. This strategic integration maximizes the return on your advertising investment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Obiquiste Solutions */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              Why Choose Obiquiste Solutions
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Partner with Amazon Brand Store experts who understand what converts.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-slate-300 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Store Portfolio */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-4 h-4 mr-2" />
              Our Portfolio
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Brand Stores We've Built
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Here are some of the Amazon Brand Stores we've successfully designed and launched for our clients. Click to explore each store.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Chef Urbano', category: 'Food & Beverages', url: 'https://www.amazon.in/stores/ChefUrbano/page/10F9C465-A237-43CA-8504-C8F6C0F103CD?ref_=ast_bln' },
              { name: 'VASU', category: 'Health & Personal Care', url: 'https://www.amazon.in/stores/VASU/page/D7323B9D-474B-49CA-8195-F6F3C5FA81A1?ref_=ast_bln' },
              { name: 'Epique', category: 'Beauty & Cosmetics', url: 'https://www.amazon.in/stores/Epique/page/FD3E0D0D-32C6-4B86-B647-E2D23C0D0067?ref_=ast_bln' },
              { name: 'Phytocid-GT', category: 'Healthcare', url: 'https://www.amazon.in/stores/Phytocid-GT/page/996578BC-4F33-439A-B20E-AA7D18CEA22C?ref_=ast_bln' },
              { name: 'CHIPCHOPS', category: 'Pet Supplies', url: 'https://www.amazon.in/stores/CHIPCHOPS/page/980726FC-053E-4888-827C-E876F65D94ED?ref_=ast_bln' },
              { name: 'EZAHK', category: 'Home & Kitchen', url: 'https://www.amazon.in/stores/EZAHK/page/A8B0DC9B-63DC-4B8B-A500-E6AEF516BD12?ref_=ast_bln' },
            ].map((store, idx) => (
              <motion.a
                key={idx}
                href={store.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-gradient-to-br from-slate-50 to-orange-50 p-6 rounded-2xl border border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                    <Store className="w-6 h-6 text-orange-600" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-800 mb-1 group-hover:text-orange-600 transition-colors">
                  {store.name}
                </h3>
                <p className="text-sm text-slate-500">{store.category}</p>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <span className="text-xs font-medium text-orange-600 flex items-center">
                    View Brand Store on Amazon
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>

          {/* Additional Sample Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="mt-8 text-center"
          >
            <a
              href="https://www.amazon.in/stores/page/91FF2F44-F8FE-4452-B754-831E218F90D3"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-slate-100 hover:bg-orange-100 text-slate-700 hover:text-orange-700 px-6 py-3 rounded-full text-sm font-medium transition-colors"
            >
              <Store className="w-4 h-4" />
              View More Brand Store Samples
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* All Amazon Brands We Work With */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-4 h-4 mr-2" />
              All Amazon Clients
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Amazon Brands We Currently Work With
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We provide end-to-end Amazon services for 20+ brands across diverse categories.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Brand</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Product / Focus</th>
                  <th className="text-left py-4 px-6 font-heading font-bold text-slate-800 border-b-2 border-slate-200">Category</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { brand: 'Nutri Hut', product: 'FMCG / Nutrition Products', category: 'FMCG / Food & Nutrition' },
                  { brand: 'Rayon Caboki', product: 'Hair/Beauty Products', category: 'Beauty & Personal Care / Hair Care' },
                  { brand: 'Ayurdaanta Herbs', product: 'Ayurvedic Herbs & Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Ayur Devam', product: 'Ayurvedic/FMCG Products', category: 'Health & Wellness / Ayurveda' },
                  { brand: 'Suparna Trikha', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Haute', product: 'Gas Stoves', category: 'Home & Kitchen / Kitchen Appliances' },
                  { brand: 'GK Trading', product: 'Wallpapers', category: 'Home Improvement / Home Décor' },
                  { brand: 'Yuri Woori', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                  { brand: 'Mooncntrl', product: 'Apparel', category: 'Fashion / Apparel' },
                  { brand: 'Indian Kitchen Foods', product: 'Ready-to-Eat Meals', category: 'FMCG / Packaged Food & Ready-to-Eat' },
                  { brand: 'Earthvya', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'GW Khamkar', product: 'FMCG Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Vintage Oils', product: 'Oils / Edible Products', category: 'FMCG / Food & Grocery' },
                  { brand: 'Fitelo', product: 'Smart Weighing Scales', category: 'Electronics / Smart Health Devices' },
                  { brand: 'Fitty', product: 'Supplements', category: 'Health & Wellness / Nutraceuticals' },
                  { brand: 'RajGharana Metals', product: 'Kitchen Utensils', category: 'Home & Kitchen / Cookware & Kitchenware' },
                  { brand: 'HII Health Inside It', product: 'Health & Wellness Products', category: 'Health & Wellness' },
                  { brand: 'Auli Lifestyle', product: 'Skincare & Beauty Products', category: 'Beauty & Personal Care / Skincare' },
                  { brand: 'EHOP', product: 'TV & Electronic Remotes', category: 'Electronics / Consumer Electronics Accessories' },
                  { brand: 'Lavenust', product: 'Beauty Products', category: 'Beauty & Personal Care' },
                ].map((item, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-slate-100 hover:bg-orange-50/50 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <span className="font-semibold text-slate-800">{item.brand}</span>
                    </td>
                    <td className="py-4 px-6 text-slate-600">{item.product}</td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        {item.category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-12 border border-orange-200">
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Ready to Build Your Amazon Brand Store?
            </h2>
            <p className="text-lg md:text-xl text-slate-700 mb-8">
              Let our experts design and optimize a professional Brand Store that showcases your products and drives conversions.
            </p>
            <Link to="/contact?service=amazon-brand-store">
              <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                Get Free Store Consultation
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonBrandStore;
